import React from 'react';
import { motion } from 'framer-motion';
import { EyeIcon, RefreshCwIcon } from 'lucide-react';
import { InteriorSlide } from './SlideShell';
import type { SlideProps } from './SlideShell';
const GOVERNANCE_DASHBOARD = 'https://cdn.magicpatterns.com/uploads/5Wc9ZZaP7QNjMKZ7z6a6XX/Screenshot_2026-09-11_at_15.57.41.png';
const METRICS = [{
  value: '6',
  label: 'Snaks live to HCPs'
}, {
  value: '14',
  label: 'Snaks in the portfolio'
}, {
  value: '7',
  label: 'Campaigns in flight'
}, {
  value: '3',
  label: 'Waiting on the brand team'
}] as const;
const LEADERSHIP_INSIGHTS = ['Everything in flight across brands and markets, on one board', 'Exactly what is waiting on the brand team, and what is with us', 'How far HCPs get before they stop, market by market', 'Which campaign the calendar should prioritise next'] as const;
export function SlideGovernanceView({}: SlideProps) {
  return <InteriorSlide surface data-id="element-396" data-mp="14urdvdgu39gk">
      <motion.header initial={{
      opacity: 0,
      y: 16
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }} className="mb-[clamp(1rem,1.7vw,1.6rem)] flex-shrink-0" data-id="element-397" data-mp="14w2e53b2lz2x">
        <p className="text-[clamp(0.7rem,1vw,0.9rem)] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-398" data-mp="v0ioi4e1d5zg">
          The Snak format &amp; data insights
        </p>
        <h2 className="mt-3 max-w-6xl text-[clamp(2rem,3.5vw,3.35rem)] font-extrabold leading-[1.02] tracking-[-0.04em] text-[var(--fg)]" data-id="element-399" data-mp="m26ya3h30a7j">
          One governance view across{' '}
          <span className="text-[var(--brand)]" data-id="element-400" data-mp="1epkozju6s9j7">brands, markets and cadence.</span>
        </h2>
        <p className="mt-3 max-w-4xl text-[clamp(0.9rem,1.15vw,1.05rem)] leading-relaxed text-[var(--muted)]" data-id="element-401" data-mp="19ykwqrw0ifn1">
          Directors see the whole portfolio; CX teams retain the market and campaign
          detail needed to act.
        </p>
      </motion.header>

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-5 lg:grid-cols-12" data-id="element-402" data-mp="22h8afm8oise3">
        <motion.section initial={{
        opacity: 0,
        y: 18
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.16,
        duration: 0.6
      }} className="flex min-h-0 flex-col gap-3 lg:col-span-8" aria-label="Brand intelligence dashboard" data-id="element-403" data-mp="1xndt2pgjqpi6">
          <figure className="min-h-0 flex-1 overflow-hidden rounded-2xl border border-black/10 bg-white shadow-sm" data-id="element-404" data-mp="27s3zp8mwahsq">
            <img src={GOVERNANCE_DASHBOARD} alt="Mission Control overview showing live Snaks, HCP sessions, median completion, items waiting on the brand team, brands, markets and campaigns in flight" className="h-full w-full object-contain object-top" data-id="element-405" data-mp="2bse1e93nmm1s" />
          </figure>
          <div className="flex flex-shrink-0 items-center gap-3 rounded-xl border border-[var(--brand)]/10 bg-white px-4 py-3" data-id="element-406" data-mp="1svczwy9b742l">
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[var(--brand)]" data-id="element-407" data-mp="3j7iwabb6z7nb">
              Computed, never editorial
            </p>
            <p className="text-xs font-semibold leading-snug text-[var(--fg)]" data-id="element-408" data-mp="3n71gf4v6vep3">
              Every status, session and completion figure comes straight from
              the live builds.
            </p>
          </div>
        </motion.section>

        <motion.aside initial={{
        opacity: 0,
        y: 18
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.28,
        duration: 0.6
      }} className="flex min-h-0 flex-col gap-3 lg:col-span-4" data-id="element-409" data-mp="1lc3lurpuyh77">
          <div className="grid grid-cols-2 gap-3" data-id="element-410" data-mp="1v86p60wg4lvq">
            {METRICS.map(metric => <article key={metric.label} className="rounded-2xl border border-[var(--brand)]/10 bg-white px-4 py-3 shadow-sm" data-id="element-411" data-mp="1qaevb1c8qpow">
                <p className="text-[clamp(1.75rem,3vw,2.75rem)] font-extrabold leading-none text-[var(--fg)]" data-id="element-412" data-mp="1zco6zuj8wp3v">
                  {metric.value}
                </p>
                <p className="mt-2 text-[10px] font-bold uppercase tracking-[0.1em] text-[var(--muted)]" data-id="element-413" data-mp="4xloigsm67ag">
                  {metric.label}
                </p>
              </article>)}
          </div>

          <article className="flex min-h-0 flex-1 flex-col rounded-2xl border border-[var(--brand)]/10 bg-white p-5 shadow-sm" data-id="element-414" data-mp="bdaygloxwpxm">
            <div className="flex items-center gap-2 text-[var(--brand)]" data-id="element-415" data-mp="ge8f2tuaxbx0">
              <EyeIcon className="h-5 w-5" aria-hidden="true" data-id="element-416" data-mp="lcjo52crvie6" />
              <h3 className="text-xs font-extrabold uppercase tracking-[0.16em]" data-id="element-417" data-mp="l9nrrigkp1ru">
                Leadership can see
              </h3>
            </div>
            <ul className="mt-4 flex flex-1 flex-col justify-around gap-3" data-id="element-418" data-mp="3ots41gnxw869">
              {LEADERSHIP_INSIGHTS.map(insight => <li key={insight} className="flex gap-3 text-xs font-semibold leading-snug text-[var(--fg)]" data-id="element-419" data-mp="3tpqmyoqi0xza">
                  <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[var(--brand)]" data-id="element-420" data-mp="2ez01xhu0num" />
                  {insight}
                </li>)}
            </ul>
            <div className="mt-4 flex items-start gap-3 border-t border-black/5 pt-4" data-id="element-421" data-mp="360eig2649e3b">
              <RefreshCwIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-[var(--brand)]" aria-hidden="true" data-id="element-422" data-mp="2yi7y0bbdh7rj" />
              <p className="text-xs leading-snug text-[var(--muted)]" data-id="element-423" data-mp="2tkephbmlk1o3">
                The view closes the loop: portfolio gap → calendar priority → campaign → new evidence.
              </p>
            </div>
          </article>
        </motion.aside>
      </div>
    </InteriorSlide>;
}